import { skipToken } from '@apollo/client/react'
import { useIntersection } from '@mantine/hooks'
import type { FormEvent } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Label as AriaLabel, Radio as AriaRadio } from 'react-aria-components'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import IcBaselineKeyboardArrowDown from '~icons/ic/baseline-keyboard-arrow-down'
import IcBaselineKeyboardArrowUp from '~icons/ic/baseline-keyboard-arrow-up'
import IcOutlineCancel from '~icons/ic/outline-cancel'
import IcOutlineCheckCircle from '~icons/ic/outline-check-circle'
import IcOutlineInfo from '~icons/ic/outline-info'
import { openSignModal } from 'src/components/Account/utils'
import NewLabel from 'src/components/NewLabel'
import { KaisuukenBadge } from 'src/features/Kaisuuken/components/KaisuukenBadge'
import { useKaisuukenMatchTraining } from 'src/features/Kaisuuken/hooks/useKaisuukenMatch'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModel } from 'src/services/apollo/generation-model'
import { useGetMeWithQuotaForCurrency } from 'src/services/apollo/quota'
import { executeCreateTrainingTask } from 'src/services/apollo/task'
import type { DynamicTrainLoraModelItem } from 'src/services/constants'
import { useDynamicTrainLoraModels } from 'src/services/constants'
import { useMe } from 'src/services/helpers/user'
import { cx } from 'src/utils/cx'
import { formatTime } from 'src/utils/dayjs'
import { getErrorCode } from 'src/utils/errorUtil'
import { getImageThumbnailUrl } from 'src/utils/media'
import { isDitV1OrMMDIT26AModel, isUserMultiLora } from 'src/utils/model'
import { useQs } from 'src/utils/qs'
import { track } from 'src/utils/tracker'
import { userHasMembership } from 'src/utils/user'
import Text from '~/components/ui/Text'
import { CircularSpinner } from '../../GenerationTask/Common/CircularSpinner'
import { TrainLoraCreditRebateBanner } from '../../GrowthUnit/TrainLoraEvent'
import { UITooltipHelpIcon } from '../../HelpIcon'
import { useImageItemListValue } from '../../Media/MediaSelectPreviewPanel/context'
import { openMembershipMoreCreditsModal } from '../../Membership/MembershipMoreCreditsModal'
import { QuotaCost } from '../../Quota/QuotaBalance'
import { UIButton } from '../../ui/Button'
import { UIDialog } from '../../ui/Modal'
import { UIRadio, UIRadioGroup } from '../../ui/RadioGroup'
import { UIInput, UITextArea } from '../../ui/TextField'
import {
  ImageItemsSourceContext,
  TrainLoraContext,
  TrainLoraDatasetContext,
  TrainLoraSubmitContext,
} from '../context'
import { TrainLoraCategories } from './TrainLoraCategories'
import { areTrainingTaskImagesReady, getTrainingTaskPrice } from '../utils'
import {
  getTrainLoraInputTrackAttrs,
  MAX_ALLOWED_TRIGGER_WORDS_LENGTH,
  MIN_TRIGGER_WORDS_LENGTH_MMDIT26A,
  validateTrainLoraInput,
} from '../validate'

const DEFAULT_VISIBLE_COUNT = 4

export default function TrainLoraParameters() {
  useSyncModelIdFromQs()

  const navigate = useNavigate()
  const { data: trainLoraModels, isLoading: trainLoraModelsLoading } =
    useDynamicTrainLoraModels()

  const [{ datasetId }] = useContext(TrainLoraDatasetContext)
  const [expand, setExpand] = useState<boolean>(false)

  const { data: userData } = useGetMeWithQuotaForCurrency({
    currency: 'free::user_lora_training',
  })
  const isMember = userHasMembership(userData)
  const quotaAmount = userData?.quotaAmount
  const isFreeToTrain = isMember && (quotaAmount ?? 0) > 0

  const { me } = useMe()

  /** Media Sources are also for data tracking */
  const imageItemsSourceRef = useContext(ImageItemsSourceContext)

  const [
    {
      title,
      triggerWords,
      versionId,
      modelType,
      importedLoraList,
      primaryLoraModelId,
      category,
    },
    setTrainLoraCtx,
  ] = useContext(TrainLoraContext)

  const imageItems = useImageItemListValue()

  const kaisuukenMatch = useKaisuukenMatchTraining(versionId)

  const trainLoraModelList = trainLoraModels?.models ?? []

  const filteredModelList = trainLoraModelList.filter(
    m => m.modelType === modelType,
  )
  const selectedModelTheme = filteredModelList.find(
    m => m.versionId === versionId,
  )?.usage

  useInitialModelSelectionEffect({
    trainLoraModelList,
    trainLoraModelsLoading,
    modelType,
  })

  const { t } = useTranslation([
    'market',
    'action',
    'artwork',
    'trainlora',
    'generation',
  ])

  const onReset = async () => {
    track('train_lora_reset_click')
    try {
      const proceed = await UIDialog.confirm({
        heading: t('market:train-lora.info.reset.dialog.title'),
        content: t('market:train-lora.info.reset.dialog.description'),
      })
      if (!proceed) return
      track('train_lora_reset_confirm_click')

      setTrainLoraCtx({
        title: '',
        triggerWords: '',
        importedLoraList: [],
        versionId: filteredModelList[0]?.versionId,
        modelType: GenerationModelType.SdxlModel,
      })
    } catch {
      // noop
    }
  }

  const actualPrice = getTrainingTaskPrice({
    prices: trainLoraModels?.pricing?.pricesByModelType,
    modelType,
    datasetId,
    primaryLoraModelId,
    isFreeToTrain,
  })

  const [, setSubmitCtx] = useContext(TrainLoraSubmitContext)
  const [submitState, onSubmit] = useAsyncFn(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!me?.id) {
        openSignModal()
        return
      }
      try {
        if (!versionId)
          throw new Error(t('market:train-lora.notice.model-needed'))
        if (!areTrainingTaskImagesReady(imageItems))
          throw new Error(t('market:train-lora.notice.uploading-images'))
        track('start_training_click')
        const input = validateTrainLoraInput(
          t,
          {
            baseModelId: versionId,
            // Remove imageItems when datasetId is set (Remove later)
            mediaIds: imageItems && !datasetId ? imageItems : [],
            title,
            type: GenerationModelType.UserMultiLora,
            triggerWords,
            primaryLoraModelId,
            trainingTaskId: datasetId,
            category,
            kaisuukenId: kaisuukenMatch.selectedKaisuukenId,
          },
          { modelType },
        )
        track('create_training_task_input', {
          ...getTrainLoraInputTrackAttrs(input, {
            trainedLoraList: importedLoraList,
            modelType,
            modelTheme: selectedModelTheme,
            credit: actualPrice,
            mediaSourceMap: imageItemsSourceRef.current ?? undefined,
          }),
        })
        const res = await executeCreateTrainingTask({
          input,
        })
        if (res.data?.createTrainingTask?.refId) {
          navigate(`/model/${res.data.createTrainingTask.refId}`)
        }
        kaisuukenMatch.refresh()
        toast.success(t('market:train-lora.notice.submit-success'))
      } catch (err: any) {
        const errCode = getErrorCode(err)
        if (errCode === 'INSUFFICIENT_BALANCE') {
          openMembershipMoreCreditsModal({
            scene: 'insufficient-credit-train-lora',
            trackingLocation: 'insufficient-credit-train-lora',
            reason: t(
              'generation:submit.user-insufficient-credit-dialog.description',
            ),
            defaultMode: 'packs',
          })
          return
        }
        toast.error(err.message)
      }
    },
    [
      title,
      triggerWords,
      versionId,
      modelType,
      imageItems,
      actualPrice,
      selectedModelTheme,
      primaryLoraModelId,
      category,
      kaisuukenMatch.selectedKaisuukenId,
      kaisuukenMatch.refresh,
    ],
  )

  useEffect(() => {
    setSubmitCtx({ isSubmitting: submitState.loading })
  }, [submitState.loading, setSubmitCtx])

  const selectedDitFamilyModel = isDitV1OrMMDIT26AModel(modelType)
  const isAddingToModel = Boolean(datasetId) || Boolean(primaryLoraModelId)
  const canExpand = filteredModelList.length > DEFAULT_VISIBLE_COUNT

  return (
    <section className="flex flex-col gap-5">
      <TrainLoraCreditRebateBanner />
      <div className="bg-white dark:bg-neutral-900 rounded-xl px-3 py-3 border border-neutral-200 dark:border-neutral-700 shadow-sm flex flex-col gap-3">
        <div className="flex w-full justify-between">
          <h2 className="text-lg font-semibold">
            {t('market:train-lora.parameters.title')}
          </h2>
          <UIButton
            variant="outlined"
            className="capitalize rounded-full"
            type="button"
            onPress={onReset}
          >
            {t('action:reset.label')}
          </UIButton>
        </div>
        <AddingVersionToModel />
        <form
          id="train-lora-form"
          className="flex flex-col gap-4"
          onSubmit={onSubmit}
        >
          {/* Core definition: name + trigger words together */}
          <div className="flex flex-col gap-1 w-full">
            <label
              htmlFor="lora-name"
              className="font-medium text-black dark:text-neutral-300"
            >
              {isAddingToModel
                ? t('market:train-lora.info.version-name.title')
                : t('market:train-lora.info.name.title')}
            </label>
            <UIInput
              id="lora-name"
              variant="rounded-normal"
              placeholder={
                isAddingToModel
                  ? t('market:train-lora.info.version-name.placeholder')
                  : t('market:train-lora.info.name.placeholder')
              }
              value={title}
              onChange={e => {
                setTrainLoraCtx(prev => ({ ...prev, title: e.target.value }))
              }}
              className="w-full"
              required
            />
          </div>
          <TriggerWordsField
            selectedDitFamilyModel={selectedDitFamilyModel}
            triggerWords={triggerWords ?? ''}
            onChange={value =>
              setTrainLoraCtx(prev => ({
                ...prev,
                triggerWords: value,
              }))
            }
          />

          <TrainLoraCategories
            value={category ?? null}
            onChange={value =>
              setTrainLoraCtx(prev => ({ ...prev, category: value }))
            }
          />

          {/* Model selection */}
          <UIRadioGroup
            orientation="horizontal"
            value={modelType}
            onChange={value => {
              const targetModelType = value as GenerationModelType
              track('train_lora_model_type_change', {
                modelType: targetModelType,
              })
              setTrainLoraCtx(prev => ({
                ...prev,
                modelType: targetModelType,
                versionId: trainLoraModelList.find(
                  it => it.modelType === targetModelType,
                )?.versionId,
              }))
            }}
          >
            <div className="flex gap-1 mb-1">
              <AriaLabel className="font-medium text-black dark:text-neutral-300">
                {t('market:train-lora.info.model-type.title')}
              </AriaLabel>
              <UITooltipHelpIcon
                variant="normal"
                TooltipProps={{
                  placement: 'top',
                }}
                iconComp={IcOutlineInfo}
              >
                <p className="text-sm max-w-sm text-neutral-600 dark:text-neutral-300">
                  <Trans
                    t={t}
                    i18nKey="market:train-lora.info.model-type.help-info.text"
                    components={{
                      a: (
                        <a
                          className="text-violet-600 dark:text-violet-400"
                          target="_blank"
                          href={t(
                            'market:train-lora.info.model-type.help-info.link',
                          )}
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  />
                </p>
              </UITooltipHelpIcon>
            </div>
            <div className="flex flex-wrap gap-x-4 items-center">
              <UIRadio
                variant="circle"
                value={GenerationModelType.Mmdit26AModel}
              >
                {t('market:train-lora.info.model-type.mmdit26amodel.label')}
                <NewLabel />
              </UIRadio>
              <UIRadio variant="circle" value={GenerationModelType.Dit7Model}>
                {t('market:train-lora.info.model-type.dit7model.label')}
              </UIRadio>
              <UIRadio variant="circle" value={GenerationModelType.SdxlModel}>
                {t('market:train-lora.info.model-type.sdxlmodel.label')}
              </UIRadio>
              <ModelTypeOtherGroup />
            </div>
          </UIRadioGroup>
          <UIRadioGroup
            value={versionId ?? ''}
            onChange={val => {
              track('train_lora_model_theme_change', { versionId: val })
              setTrainLoraCtx(prev => ({ ...prev, versionId: val }))
            }}
            label={
              <div className="mb-1">
                <AriaLabel className="font-medium text-black dark:text-neutral-300">
                  {t('market:train-lora.info.model-theme.title')}
                </AriaLabel>
              </div>
            }
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-[630px] mx-auto">
              {filteredModelList.slice(0, DEFAULT_VISIBLE_COUNT).map(m => (
                <ModelThemeRadioImage
                  labelDisplay={'name'}
                  key={m.versionId}
                  trainLoraModel={m}
                />
              ))}
            </div>

            {canExpand && (
              <UIButton
                className={cx('w-full py-1 my-1')}
                onPress={() => {
                  track('train_lora_models_expand_toggle', {
                    expanded: !expand,
                  })
                  setExpand(prev => !prev)
                }}
                variant="ghost"
              >
                <span className="flex items-center">
                  {expand ? (
                    <IcBaselineKeyboardArrowUp className="size-6" />
                  ) : (
                    <IcBaselineKeyboardArrowDown className="size-6" />
                  )}
                  <span className="text-sm">
                    {expand ? t('artwork:show.less') : t('artwork:show.more')}
                  </span>
                </span>
              </UIButton>
            )}
            <div
              className={cx(
                !expand && 'hidden',
                'grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-[630px] mx-auto',
              )}
            >
              {filteredModelList.slice(DEFAULT_VISIBLE_COUNT).map(m => (
                <ModelThemeRadioImage
                  labelDisplay={'name'}
                  key={m.versionId}
                  trainLoraModel={m}
                />
              ))}
            </div>
          </UIRadioGroup>
        </form>
      </div>
    </section>
  )
}

function TrainLoraPrice() {
  const [{ modelType, primaryLoraModelId }] = useContext(TrainLoraContext)
  const [{ datasetId }] = useContext(TrainLoraDatasetContext)
  const { data: trainLoraModels, isValidating } = useDynamicTrainLoraModels()
  const { data: userData, loading: isQuotaLoading } =
    useGetMeWithQuotaForCurrency({
      currency: 'free::user_lora_training',
    })
  const prices = trainLoraModels?.pricing?.pricesByModelType

  const isMember = userHasMembership(userData)
  const quotaAmount = userData?.quotaAmount
  const isFreeToTrain = isMember && (quotaAmount ?? 0) > 0

  const priceForModel = modelType ? prices?.[modelType] : undefined
  const originalPrice = priceForModel?.originalPrice

  const actualPrice = getTrainingTaskPrice({
    prices,
    modelType,
    datasetId,
    primaryLoraModelId,
    isFreeToTrain,
  })

  return (
    <>
      <QuotaCost
        loading={isValidating || isQuotaLoading}
        iconClassName="size-4"
        valueClassName={'text-white'}
        value={actualPrice}
        className="text-white"
      />
      {originalPrice && (
        <span className="text-white font-quicksand ml-2 line-through">
          {originalPrice.toLocaleString()}
        </span>
      )}
    </>
  )
}

export function TrainLoraSubmitBar() {
  const { t } = useTranslation(['market', 'trainlora'])
  const [{ isSubmitting }] = useContext(TrainLoraSubmitContext)
  const [{ versionId }] = useContext(TrainLoraContext)
  const kaisuukenMatch = useKaisuukenMatchTraining(versionId)
  const { data: userData } = useGetMeWithQuotaForCurrency({
    currency: 'free::user_lora_training',
  })
  const isMember = userHasMembership(userData)
  const quotaAmount = userData?.quotaAmount
  const isFreeToTrain = isMember && (quotaAmount ?? 0) > 0

  const { ref: sentinelRef, entry } = useIntersection({ threshold: 0 })
  const hidden = entry?.isIntersecting ?? false
  useEffect(() => {
    const sentinel = document.getElementById('train-lora-footer-sentinel')
    if (sentinel) sentinelRef(sentinel)
  }, [sentinelRef])

  return (
    <div
      className={cx(
        'fixed bottom-0 left-0 right-0 z-10 bg-background border-t border-neutral-200 dark:border-neutral-700 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] transition-transform duration-300',
        hidden && 'translate-y-full',
      )}
    >
      <div className="max-w-screen-2xl mx-auto px-5 py-3 flex items-center justify-end gap-4">
        <div className="relative group">
          <button
            type="submit"
            form="train-lora-form"
            disabled={isSubmitting || kaisuukenMatch.isChecking}
            className="text-white bg-violet-600 rounded-full font-medium text-sm px-8 py-3 disabled:opacity-40 disabled:cursor-wait flex items-center justify-center gap-3 hover:bg-violet-700 transition-colors"
          >
            {kaisuukenMatch.totalMatching > 0
              ? t('trainlora:submit-form.submit.free-training.label')
              : t('market:train-lora.submit.label')}
            {kaisuukenMatch.totalMatching > 0 ? (
              <KaisuukenBadge
                totalMatching={kaisuukenMatch.totalMatching}
                isChecking={kaisuukenMatch.isChecking}
                color="white"
              />
            ) : (
              <TrainLoraPrice />
            )}
          </button>
          <div
            role="tooltip"
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-neutral-800 dark:bg-neutral-200 text-white dark:text-neutral-800 text-xs whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          >
            {t('market:train-lora.info.estimated-time')}
          </div>
          {isFreeToTrain && (quotaAmount ?? 0) > 0 && (
            <span className="absolute top-0 -translate-y-1/2 right-3 rounded-full bg-gradient-to-r from-[#02c0a4] via-[#0282de] via-80% to-[#714FFE] px-2 py-0.5 text-white font-medium text-xs">
              {t('market:train-lora.info.free-training-times.label', {
                amount: quotaAmount,
              })}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

function TriggerWordsField(props: {
  selectedDitFamilyModel: boolean
  triggerWords: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation(['market'])
  const { selectedDitFamilyModel, triggerWords, onChange } = props
  const charCount = triggerWords.length
  const needsMinLength = selectedDitFamilyModel
  const minLength = MIN_TRIGGER_WORDS_LENGTH_MMDIT26A
  const meetsMin = !needsMinLength || charCount >= minLength
  const [showError, setShowError] = useState(false)

  return (
    <div className="flex flex-col gap-1 w-full">
      <label
        htmlFor="lora-trigger-words"
        className="flex items-center gap-1 font-medium dark:text-neutral-300"
      >
        {t('market:train-lora.info.trigger-words.title')}
        <UITooltipHelpIcon
          variant="normal"
          TooltipProps={{
            placement: 'top',
          }}
          iconComp={IcOutlineInfo}
        >
          <p className="text-sm max-w-sm text-neutral-600 dark:text-neutral-300">
            <Text
              i18nKey="market:train-lora.info.trigger-words.help-info.text"
              components={{
                a: (
                  <a
                    className="text-violet-600 dark:text-violet-400"
                    target="_blank"
                    href={t(
                      'market:train-lora.info.trigger-words.help-info.link',
                    )}
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </p>
        </UITooltipHelpIcon>
      </label>
      <div className="relative">
        <UITextArea
          id="lora-trigger-words"
          variant="rounded-normal"
          placeholder={t('market:train-lora.info.trigger-words.placeholder', {
            context: selectedDitFamilyModel ? 'dit-model' : undefined,
          })}
          value={triggerWords}
          onChange={e => {
            onChange(e.target.value)
            if (showError) setShowError(false)
          }}
          onBlur={() => {
            if (needsMinLength && !meetsMin && charCount > 0) setShowError(true)
          }}
          maxLength={MAX_ALLOWED_TRIGGER_WORDS_LENGTH}
          className={cx(
            'w-full resize-none',
            showError && '!border-red-500 !ring-red-200 dark:!ring-red-900',
          )}
          rows={2}
          required
        />
        {needsMinLength && (
          <span
            className={cx(
              'absolute right-3 bottom-2 text-[11px] font-medium pointer-events-none',
              meetsMin
                ? 'text-green-500'
                : charCount > 0
                  ? 'text-amber-500'
                  : 'text-neutral-400',
            )}
          >
            {charCount} / {minLength}
          </span>
        )}
      </div>
      {selectedDitFamilyModel ? (
        <p
          className={cx(
            'mt-2 rounded-xl text-amber-800 bg-amber-500/10 dark:text-amber-400 dark:bg-amber-400/5 text-xs whitespace-pre-line overflow-hidden transition-all duration-300',
            meetsMin && charCount > 0
              ? 'max-h-10 opacity-60 px-4 py-2'
              : 'max-h-96 px-4 py-3',
          )}
        >
          {t('market:train-lora.info.trigger-words.description', {
            context: 'dit-model',
          })}
        </p>
      ) : (
        <p className="text-neutral-500 dark:text-neutral-400 text-xs mt-1">
          {t('market:train-lora.info.trigger-words.description')}
        </p>
      )}
    </div>
  )
}

function ModelThemeRadioImage(props: {
  labelDisplay: 'theme' | 'name'
  trainLoraModel: DynamicTrainLoraModelItem
}) {
  const { t } = useTranslation(['market'])
  const { trainLoraModel, labelDisplay } = props
  const { coverUrl, versionId, modelName, usage } = trainLoraModel

  return (
    <AriaRadio
      value={versionId}
      className={({ isSelected }) =>
        cx(
          'relative rounded-lg overflow-hidden cursor-pointer min-w-0 border-2 transition-all',
          isSelected
            ? 'border-violet-600 ring-2 ring-violet-100 dark:ring-violet-900'
            : 'border-transparent hover:border-neutral-300 dark:hover:border-neutral-600',
        )
      }
    >
      {({ isSelected }) => (
        <>
          <div className="aspect-square bg-neutral-100 dark:bg-neutral-800">
            <img src={coverUrl} alt="" className="object-cover w-full h-full" />
          </div>
          <div className="absolute bottom-0 inset-x-0 bg-white/90 dark:bg-neutral-900/90 backdrop-blur-sm px-1.5 py-1 text-center">
            <span className="text-xs font-medium leading-tight truncate block">
              {labelDisplay === 'theme'
                ? t(`market:train-lora.info.model-theme.${usage}.label`)
                : modelName}
            </span>
          </div>
          {isSelected && (
            <div className="absolute top-1 right-1 bg-violet-600 rounded-full p-0.5">
              <IcOutlineCheckCircle className="size-4 text-white" />
            </div>
          )}
        </>
      )}
    </AriaRadio>
  )
}

function ModelTypeOtherGroup() {
  const { t } = useTranslation(['market', 'trainlora'])
  const [{ modelType }] = useContext(TrainLoraContext)
  const [showOther, setShowOther] = useState(
    modelType === GenerationModelType.SdV1Model,
  )
  const isOtherSelected = modelType === GenerationModelType.SdV1Model

  return showOther || isOtherSelected ? (
    <UIRadio variant="circle" value={GenerationModelType.SdV1Model}>
      {t('market:train-lora.info.model-type.sdv1model.label')}
    </UIRadio>
  ) : (
    <button
      type="button"
      className="text-sm text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
      onClick={() => {
        track('train_lora_other_model_type_click')
        setShowOther(true)
      }}
    >
      {t('trainlora:submit-form.info.model-type.other.label')}
    </button>
  )
}

function useSyncModelIdFromQs() {
  const [{ addToModelId }, setQs] = useQs()
  const {
    data: model,
    loading,
    error,
  } = useGetGenerationModel(addToModelId || skipToken)
  const [, setTrainLoraCtx] = useContext(TrainLoraContext)

  useEffect(() => {
    if (loading) return
    if (model && isUserMultiLora(model?.type)) {
      setTrainLoraCtx(prev => ({
        ...prev,
        primaryLoraModelId: addToModelId,
      }))
    }
    setQs(prev => ({ ...prev, addToModelId: null }))
  }, [loading, error, model?.type])
}

function useInitialModelSelectionEffect(args: {
  trainLoraModelsLoading: boolean
  trainLoraModelList: DynamicTrainLoraModelItem[]
  modelType?: GenerationModelType
}) {
  const [, setTrainLoraCtx] = useContext(TrainLoraContext)
  const hasSetInitialSelection = useRef(false)
  const [{ trainWithModelId }, setQs] = useQs()
  const { trainLoraModelsLoading, trainLoraModelList, modelType } = args

  useEffect(() => {
    if (hasSetInitialSelection.current) return
    if (trainLoraModelsLoading) return

    if (trainLoraModelList) {
      // First version with default modelType
      let versionId = trainLoraModelList.find(
        m => m.modelType === modelType,
      )?.versionId
      let updatedModelType = modelType

      // Override default selected train model and modelType
      if (trainWithModelId) {
        // Ensure this modelId is within the list
        const targetModel = trainLoraModelList.find(
          m => m.versionId === trainWithModelId,
        )
        // Update when found
        if (targetModel) {
          updatedModelType = targetModel.modelType
          versionId = trainWithModelId
        }
        setQs(prev => ({ ...prev, trainWithModelId: null }))
      }

      setTrainLoraCtx(prev => ({
        ...prev,
        versionId,
        modelType: updatedModelType,
      }))
      // Ensure only set once
      hasSetInitialSelection.current = true
    }
  }, [trainLoraModelsLoading, modelType, trainWithModelId, trainLoraModelList])
}

function AddingVersionToModel() {
  const [{ primaryLoraModelId }, setTrainLoraCtx] = useContext(TrainLoraContext)
  const { t } = useTranslation(['common', 'market'])

  const { data: model, loading } = useGetGenerationModel(
    primaryLoraModelId || skipToken,
  )

  if (loading)
    return (
      <div className="w-full">
        <CircularSpinner />
      </div>
    )

  if (!primaryLoraModelId || !model) return null

  const modelImage = getImageThumbnailUrl(model.mediaId)

  return (
    <div className="flex flex-col gap-2 w-full mt-3 mb-2">
      <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
        {t('market:train-lora.parameters.adding-to-model.title')}
      </h3>
      <div className="relative flex flex-row items-center justify-start px-3 py-2 bg-background-light rounded-lg gap-3">
        <div className="shrink-0 border border-px rounded-xl w-16 h-16 mb-1">
          {modelImage && (
            <img
              src={modelImage}
              alt=""
              loading="lazy"
              decoding="async"
              className="rounded-xl w-full h-full object-cover"
            />
          )}
        </div>
        <div className="min-w-0">
          <h3 className="truncate">{model.title}</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-300">
            {t('common:updated-at')}: {formatTime(model.updatedAt)}
          </p>
        </div>
        <button
          type="button"
          className="absolute top-1 right-1 p-1 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-600 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
          onClick={() => {
            setTrainLoraCtx(prev => ({
              ...prev,
              primaryLoraModelId: undefined,
            }))
          }}
        >
          <IcOutlineCancel className="size-4" />
        </button>
      </div>
    </div>
  )
}
